import { useState, useEffect, useCallback, useRef } from 'react'

/**
 * Hook per polling automatico dei dati
 * 
 * @param {Function} fetchFunction - Funzione che recupera i dati
 * @param {number} interval - Intervallo in millisecondi (default 7000ms)
 * @param {boolean} enabled - Se il polling è attivo
 * @returns {Object} { data, loading, error, lastUpdated, refresh }
 */
export function usePolling(fetchFunction, interval = 7000, enabled = true) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [lastUpdated, setLastUpdated] = useState(null)
  const [isVisible, setIsVisible] = useState(true)
  const intervalRef = useRef(null)
  const fetchRef = useRef(fetchFunction)

  // Aggiorna ref quando cambia fetchFunction
  useEffect(() => {
    fetchRef.current = fetchFunction
  }, [fetchFunction])

  // Gestisce visibilità pagina (stop polling quando in background)
  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsVisible(document.visibilityState === 'visible')
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [])

  // Funzione di fetch
  const doFetch = useCallback(async (showLoading = false) => {
    if (showLoading) setLoading(true)
    
    try {
      const result = await fetchRef.current()
      setData(result)
      setLastUpdated(new Date())
      setError(null)
    } catch (err) {
      setError(err)
      console.error('Polling error:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  // Refresh manuale
  const refresh = useCallback(() => {
    return doFetch(true)
  }, [doFetch])

  // Setup polling
  useEffect(() => {
    if (!enabled) return

    // Fetch iniziale
    doFetch(true)

    // Setup intervallo solo se visibile
    if (isVisible) {
      intervalRef.current = setInterval(() => {
        doFetch(false)
      }, interval)
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [enabled, interval, isVisible, doFetch])

  return { data, loading, error, lastUpdated, refresh }
}

export default usePolling
